import {
  Badge, Button, Card, Segmented, Select, Table, Tag, Typography, Input, DatePicker,
  Statistic, Divider, Form, Space, Tooltip, Row, Col, Menu, Dropdown,
} from "antd";
import {
  CalendarOutlined, ClockCircleOutlined, FileTextOutlined, DatabaseOutlined, BarChartOutlined,
  EditOutlined, EyeOutlined, EyeInvisibleOutlined, PlusOutlined, DownOutlined,
  SendOutlined, CheckCircleOutlined, CloseCircleOutlined, DollarOutlined,
  LineChartOutlined, WalletOutlined, BankOutlined, ExclamationCircleOutlined,
} from "@ant-design/icons";

import { SearchOutlined, FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";
import DashboardSummary from "./DashboardSummary";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { useNavigate } from "react-router-dom";
import { QuotationService } from "../../Services/Manage/QuotationService";
import useMessage from "antd/es/message/useMessage";
import { UserService } from "../../Services/Manage/UserService";
import { ExpenseServices } from "../../Services/Manage/ExpenseServices";


dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const QuotationPage = () => {
  const perm = getPermission('/quotation');
  const invoicePerm = getPermission('/invoice');
  const viewAll = perm.view_all === true;
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [segment, setSegment] = useState("Active");
  const [message, msgContext] = useMessage();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("updated_on");
  const [activeFilter, setActiveFilter] = useState({ type: 'quote', value: 'all' });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [quotationStatusFilter, setQuotationStatusFilter] = useState(null);
  const [serviceFilter, setServiceFilter] = useState(null);
  const [salesExecFilter, setSalesExecFilter] = useState(null);
  const [users, setUsers] = useState([]);
  const [dashboardDateLabel, setDashboardDateLabel] = useState(null);
  const [dashboardDateRange, setDashboardDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  /** 🔹 Fetch all quotations */
/** 🔹 Fetch all quotations */
  const getAllQuo = async () => {
    try {
      const user = await UserService.getOmeUser();
      const requests = await QuotationService.getAllQuotation();
      let allData = requests.data || [];

      const userRes = await UserService.getAllUsers();
      const userList = Array.isArray(userRes) ? userRes : (userRes?.data || []);
      setUsers(userList);

      if (!viewAll) {
        const omeUser = JSON.parse(localStorage.getItem("ome_user") || "{}");
        const userName = omeUser?.name || omeUser?.user_name || "";
        allData = allData.filter((q) => {
          const assignedNames = (q.assigned_to || "").split(',').map(s => s.trim()).filter(Boolean);
          if (assignedNames.length > 0) {
            return assignedNames.includes(userName);
          }
          // No one assigned — only mine if I created it
          return String(q.added_by ?? "").trim() === String(user.uid).trim();
        });
      }

      setData(allData);
      applyAllFilters(allData, segment, searchText, dateRange, dateFilterType);
    } catch (error) {
      console.error("Error fetching quotations:", error);
    }
  };

  useEffect(() => {
    getAllQuo();
    getAllInvoicesForPayments();
  }, []);

  // Invoices get their OWN qu_no, separate from the source quotation's qu_no
  // (linked via source_qu_no). Payments live on the invoice, not the
  // quotation, so collected/pending amounts must be computed by matching
  // each quotation to its invoice(s) via source_qu_no (or qu_no for manual
  // invoices), not by looking up payments directly under the quotation's qu_no.
  const getAllInvoicesForPayments = async () => {
    try {
      const { InvoiceService } = await import("../../Services/Manage/InvoiceService");
      const res = await InvoiceService.getAllInvoice();
      setAllPayments(res?.data || []);
    } catch (error) {
      console.error("Error fetching invoices for payments:", error);
    }
  };

  /** 🔹 Apply filtering */
  const applyAllFilters = (allData, seg, search, range, dateType, quStatus = null, salesExec = null, svc = null) => {
    let result = allData;

    if (seg) result = result.filter(item => item.status?.toLowerCase() === seg.toLowerCase());

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(item =>
        item.customer_name?.toLowerCase().includes(lower) ||
        item.qu_no?.toLowerCase().includes(lower) ||
        item.eq_no?.toLowerCase().includes(lower)
      );
    }

    if (range && range.length === 2) {
      const [start, end] = range;
      result = result.filter(item => {
        const selectedDate = new Date(item[dateType]);
        if (isNaN(selectedDate)) return false;
        return selectedDate >= start.startOf("day").toDate() && selectedDate <= end.endOf("day").toDate();
      });
    }

    if (quStatus) result = result.filter(item => (item.quotation_status || "").toLowerCase() === quStatus);
    if (salesExec) result = result.filter(item => item.added_by === salesExec);
    if (svc) result = result.filter(item => (item.line_items || []).some(it => it.service_name === svc));

    result.sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on));
    setFilteredData(result);
  };

  /** 🔹 Handlers */
  const handleSegmentChange = (val) => {
    setSegment(val);
    applyAllFilters(data, val, searchText, dateRange, dateFilterType);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    applyAllFilters(data, segment, value, dateRange, dateFilterType, quotationStatusFilter, salesExecFilter, serviceFilter);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    applyAllFilters(data, segment, searchText, range, dateFilterType);
  };

  const handleDateFilterTypeChange = (value) => {
    setDateFilterType(value);
    applyAllFilters(data, segment, searchText, dateRange, value);
  };

  /** 🔹 Status counts */
  const statusCounts = data.reduce((acc, cur) => {
    const key = cur.status || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const styles = {
    badge: {
      backgroundColor: "#1b1660ff",
      fontSize: 14,
      minWidth: 24,
      height: 24,
      lineHeight: "24px",
      marginLeft: 10,
    },
  };

  const getQuotationFollowUps = (record) => {
    if (Array.isArray(record.follow_ups) && record.follow_ups.length > 0) {
      return record.follow_ups;
    }

    if (record.n_follow_up || record.followup_current_date || record.summary) {
      return [
        {
          follow_date: record.n_follow_up || null,
          current_date: record.followup_current_date || null,
          summary: record.summary || "-",
        },
      ];
    }

    return [];
  };

  const getLatestFollowUpDate = (record) => {
    const followUps = getQuotationFollowUps(record);
    const lastItem = followUps[followUps.length - 1];
    return lastItem?.follow_date || null;
  };

  /** 🔹 Status tag colors */
  const quStatusTag = {
    draft: { color: "#8c8c8c", bg: "#fafafa" },
    sent: { color: "#1677ff", bg: "#e6f4ff" },
    viewed: { color: "#722ed1", bg: "#f9f0ff" },
    negotiation: { color: "#fa8c16", bg: "#fff7e6" },
    approved: { color: "#52c41a", bg: "#f6ffed" },
    converted: { color: "#13c2c2", bg: "#e6fffb" },
    rejected: { color: "#f5222d", bg: "#fff1f0" },
  };



  /** 🔹 Table Columns */
  const columns = [
    {
      title: "S. No",
      align: "center",
      width: 70,
      render: (_, __, index) => {
        const page = 1; // pagination handled by antd, index resets per page
        return index + 1;
      },
    },
    {
      title: "Quotation No",
      dataIndex: "qu_no",
      render: (v) => <span style={{ color: "#1677ff", fontWeight: 600 }}>{v}</span>,
    },
    {
      title: "Quotation Date",
      dataIndex: "date",
      render: (text) => (text ? dayjs(text).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      render: (v) => v || "-",
    },
    {
      title: "Service / Requirement",
      key: "service",
      render: (_, record) => {
        const items = record.line_items || [];
        return items.length ? items[items.length - 1].service_name : "-";
      },
    },
    {
      title: "Value",
      key: "value",
      align: "right",
      render: (_, record) => Number(record.grand_total || 0).toLocaleString("en-IN"),
    },
    {
      title: "Status",
      key: "quotation_status",
      align: "center",
      render: (_, record) => {
        const st = (record.quotation_status || "Draft");
        const t = quStatusTag[st.toLowerCase()] || quStatusTag.draft;
        return (
          <span style={{ background: t.bg, color: t.color, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 12 }}>
            {st}
          </span>
        );
      },
    },
    {
      title: "Probability",
      key: "probability",
      align: "center",
      render: (_, record) => {
        return record.probability != null ? `${record.probability}%` : "-";
      },
    },
    {
      title: "Valid Till",
      key: "valid_till",
      render: (_, record) => {
        const v = record.valid_date || record.expected_close_date;
        return v ? dayjs(v).format("DD-MM-YYYY") : "-";
      },
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_to",
      render: (a) => {
        if (!a) return "-";
        const byUid = users.find(u => u.uid === a);
        return byUid ? (byUid.name || byUid.full_name || byUid.username || a) : a;
      },
    },

    ...(viewAll ? [{
      title: "Created By",
      dataIndex: "prepared_by",
      render: (v) => v || "-",
    }] : []),
    {
      title: "Actions",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            size="middle"
            icon={expandedRowKeys.includes(record.qu_no) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() =>
              setExpandedRowKeys((prev) =>
                prev.includes(record.qu_no)
                  ? prev.filter((key) => key !== record.qu_no)
                  : [...prev, record.qu_no]
              )
            }
          />
          <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
            <Button
              size="middle"
              icon={<EditOutlined />}
              onClick={() => navigate("/quotation/edit", { state: { qu_no: record.qu_no } })}
              disabled={!perm.edit}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const followUps = getQuotationFollowUps(record);

    return (
      <div
        style={{
          width: "520px",
          marginLeft: "40px",
          background: "#fafafa",
          border: "1px solid #e5e5e5",
          borderRadius: "8px",
          padding: "10px",
        }}
      >
        <Typography.Text strong style={{ fontSize: 13 }}>
          Follow-up Details
        </Typography.Text>

        {followUps.map((item, index) => (
          <div
            key={index}
            style={{
              marginTop: "8px",
              padding: "8px",
              background: "#fff",
              borderRadius: "6px",
              border: "1px solid #eee",
            }}
          >
            <div style={{ display: "flex", gap: "20px", fontSize: "12px" }}>
              <span>
                <b>Follow-up:</b>{" "}
                {item.follow_date
                  ? dayjs(item.follow_date).format("DD-MM-YYYY")
                  : "-"}
              </span>

              <span>
                <b>Added On:</b>{" "}
                {item.current_date
                  ? dayjs(item.current_date).format("DD-MM-YYYY")
                  : "-"}
              </span>
            </div>

            <div style={{ marginTop: "5px", fontSize: "12px" }}>
              <b>Summary:</b> {item.summary || "-"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ADD before return (
  const getQuickStats = (status) => {
    const today = dayjs().startOf("day");
    const last7 = dayjs().subtract(6, "day").startOf("day");
    const last30 = dayjs().subtract(29, "day").startOf("day");
    const statusData = data.filter(d => d.status?.toLowerCase() === status.toLowerCase());
    return {
      today: statusData.filter(d => d.added_on && dayjs(d.added_on).isAfter(today)).length,
      last7: statusData.filter(d => d.added_on && dayjs(d.added_on).isAfter(last7)).length,
      last30: statusData.filter(d => d.added_on && dayjs(d.added_on).isAfter(last30)).length,
      total: statusData.length,
    };
  };

  const getFollowUpStats = (status) => {
    const todayStart = dayjs().startOf("day");
    const todayEnd = dayjs().endOf("day");
    const next7End = dayjs().add(7, "day").endOf("day");
    const next30End = dayjs().add(30, "day").endOf("day");
    const statusData = data.filter(d => d.status?.toLowerCase() === status.toLowerCase());
    const withFollowUp = statusData.filter(d => d.n_follow_up);
    return {
      today: withFollowUp.filter(d =>
        dayjs(d.n_follow_up).isBetween(todayStart, todayEnd, null, "[]")
      ).length,
      next7: withFollowUp.filter(d =>
        dayjs(d.n_follow_up).isAfter(todayEnd) &&
        dayjs(d.n_follow_up).isSameOrBefore(next7End)
      ).length,
      next30: withFollowUp.filter(d =>
        dayjs(d.n_follow_up).isAfter(todayEnd) &&
        dayjs(d.n_follow_up).isSameOrBefore(next30End)
      ).length,
      total: withFollowUp.length,
    };
  };

  const applyQuickDateFilter = (days) => {
    const end = dayjs();
    const start = days === 0 ? end.startOf("day") : end.subtract(days - 1, "day").startOf("day");
    const range = [start, end];
    setDateFilterType("added_on");
    setDateRange(range);
    applyAllFilters(data, segment, searchText, range, "added_on");
  };

  const applyFollowUpDateFilter = (days) => {
    const start = days === 0 ? dayjs().startOf("day") : dayjs().endOf("day");
    const end = days === 0 ? dayjs().endOf("day") : dayjs().add(days, "day").endOf("day");
    const range = [start, end];
    setDateFilterType("n_follow_up");
    setDateRange(range);
    applyAllFilters(data, segment, searchText, range, "n_follow_up");
  };

  const clearDateFilters = () => {
    setDateRange(null);
    setDateFilterType("updated_on");
    setQuotationStatusFilter(null);
    setServiceFilter(null);
    setSalesExecFilter(null);
    setSearchText("");
    setActiveFilter({ type: 'quote', value: 'all' });
    applyAllFilters(data, segment, "", null, "updated_on", null, null, null);
  };

  const quickStats = getQuickStats(segment);

  const serviceOptions = [
    ...new Set(
      data.flatMap(d => (d.line_items || []).map(it => it.service_name).filter(Boolean))
    )
  ].map(s => ({ label: s, value: s }));

  const salesExecOptions = [
    ...new Set(data.map(d => d.added_by).filter(Boolean))
  ].map(s => ({ label: s, value: s }));


  const REPORT_COLUMNS = [
    { label: "Quotation No" },
    { label: "Date" },
    { label: "Customer" },
    { label: "Service" },
    { label: "Value (Rs.)" },
    { label: "Status" },
    { label: "Assigned To" },
  ];

const buildReportRows = () => filteredData
    .filter(d => {
      if (!dashboardDateRange) return true;
      const date = dayjs(d.added_on);
      return date.isValid()
        && date.isSameOrAfter(dashboardDateRange[0], 'day')
        && date.isSameOrBefore(dashboardDateRange[1], 'day');
    })
    .map((d) => {
      const items = d.line_items || [];
      const service = items.length ? items[items.length - 1].service_name : "-";
      const assignedUser = users.find(u => u.uid === d.assigned_to);
      return {
        "Quotation No": d.qu_no || "-",
        "Date": d.date ? dayjs(d.date).format("DD-MM-YYYY") : "-",
        "Customer": d.customer_name || "-",
        "Service": service || "-",
        "Value (Rs.)": Number(d.grand_total || 0),
        "Status": d.quotation_status || "Draft",
        "Assigned To": assignedUser ? (assignedUser.name || assignedUser.full_name || assignedUser.username || d.assigned_to) : (d.assigned_to || "-"),
      };
    });

  const reportDateStamp = () => dayjs().format("YYYY-MM-DD");

  const handleExportExcel = () => {
    const rows = buildReportRows();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Quotations Report");
    XLSX.writeFile(wb, `Quotations-Report-${reportDateStamp()}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = buildReportRows();
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Quotations Report", 14, 14);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
    autoTable(doc, {
      startY: 26,
      head: [REPORT_COLUMNS.map((c) => c.label)],
      body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 88, 180] },
    });
    doc.save(`Quotations-Report-${reportDateStamp()}.pdf`);
  };

  const exportMenuItems = [
    { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
    { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
  ];

  const followUpStats = getFollowUpStats(segment);

  const dashData = dashboardDateRange
    ? data.filter(d => {
      const date = dayjs(d.added_on);
      return date.isValid()
        && date.isSameOrAfter(dashboardDateRange[0], 'day')
        && date.isSameOrBefore(dashboardDateRange[1], 'day');
    })
    : data;

  // ---- Dashboard stat counts ----
  const countByQuStatus = (st) =>
    dashData.filter(d => (d.quotation_status || "").toLowerCase() === st.toLowerCase()).length;

  const draftCount = countByQuStatus("Draft");
  const sentCount = countByQuStatus("Sent");
  const viewedCount = countByQuStatus("Viewed");
  const approvedCount = countByQuStatus("Approved");
  const rejectedCount = countByQuStatus("Rejected");
  const totalQuotations = data.length;
  // payment-based totals
  // group payments by qu_no
// allPayments now actually holds invoices (each with its own .payments array).
  // Group invoices by the quotation qu_no they belong to: an invoice generated
  // from a quotation carries source_qu_no === that quotation's qu_no; a manual
  // invoice's own qu_no may equal the quotation's qu_no directly (rare/manual case).
  const invoicesByQu = allPayments.reduce((acc, inv) => {
    const key = inv.source_qu_no || inv.qu_no;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(inv);
    return acc;
  }, {});

  const collectedForQuotation = (quNo) => {
    const invoices = invoicesByQu[quNo] || [];
    return invoices.reduce(
      (sum, inv) => sum + (inv.payments || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0),
      0
    );
  };

  // payment-based totals
  const paymentTotals = dashData.reduce((acc, d) => {
    const collected = collectedForQuotation(d.qu_no);
    const quTotal = Number(d.grand_total || 0);    // from quotation line items
    const st = (d.quotation_status || "").toLowerCase();
    const isApproved = st === "approved";
    const isConverted = st === "converted";

    acc.totalValue += quTotal;                      // all quotations (real quotation total)
    if (isConverted) acc.expectedRevenue += quTotal; // expected revenue = converted only
    // Collection only makes sense once a quotation has actually converted to
    // an invoice — "Approved" alone has no invoice/payments yet, so collected
    // amount is always 0 for those. Use Converted (which has linked invoices).
    if (isConverted) {
      acc.collected += collected;
      acc.pending += Math.max(quTotal - collected, 0);
    }
    return acc;
  }, { totalValue: 0, collected: 0, pending: 0, expectedRevenue: 0 });

  // Conversion rate = won deals (Converted) ÷ total quotations, matching the
  // definition used in the Sales Dashboard/Report elsewhere in the app.
  const convertedCount = countByQuStatus("Converted");
  const conversionRate = totalQuotations
    ? ((convertedCount / totalQuotations) * 100).toFixed(0)
    : 0;

  // ---- Pipeline (workflow stages) ----
  const pipelineStages = ["Draft", "Sent", "Viewed", "Negotiation", "Approved", "Converted"];
  const pipelineColors = ["#1677ff", "#13c2c2", "#722ed1", "#fa8c16", "#52c41a", "#13c2c2"];
  const pipeline = pipelineStages.map((label, i) => ({
    label,
    count: dashData.filter(d => (d.quotation_status || "").toLowerCase() === label.toLowerCase()).length,
    color: pipelineColors[i % pipelineColors.length],
  }));
  const pipelineMax = Math.max(...pipeline.map(p => p.count), 1);

  // ---- Status Distribution (donut) ----
  const distColors = ["#1677ff", "#13c2c2", "#722ed1", "#fa8c16", "#52c41a", "#f5222d"];
  const distStages = ["Draft", "Sent", "Viewed", "Negotiation", "Approved", "Rejected"];
  const statusDistData = distStages.map((name, i) => ({
    name,
    value: dashData.filter(d => (d.quotation_status || "").toLowerCase() === name.toLowerCase()).length,
    color: distColors[i % distColors.length],
  }));
  const distTotal = statusDistData.reduce((a, b) => a + b.value, 0);

  // ---- Monthly Quotation Value (by month, from grand_total) ----
  const monthlyMap = {};
  dashData.forEach(d => {
    const key = dayjs(d.added_on).format("MMM");
    monthlyMap[key] = (monthlyMap[key] || 0) + Number(d.grand_total || 0);
  });
  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthlyData = monthOrder
    .filter(m => monthlyMap[m] !== undefined)
    .map(m => ({ month: m, value: monthlyMap[m] }));

  // ---- Service/Category Wise Quotation Value (by business_type) ----
  // ---- Service Wise Quotation Value (by real service_name from line items) ----
  const svcColors = ["#1677ff", "#13c2c2", "#722ed1", "#fa8c16", "#f5222d", "#bfbfbf"];
  const svcMap = {};
  dashData.forEach(d => {
    (d.line_items || []).forEach(it => {
      const key = it.service_name || "Others";
      svcMap[key] = (svcMap[key] || 0) + Number(it.totalprice || 0);
    });
  });
  const svcTotal = Object.values(svcMap).reduce((a, b) => a + b, 0);

  // sort desc, take top 5, roll the rest into "Others" (always last)
  const svcSorted = Object.entries(svcMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const svcTop = svcSorted.slice(0, 5);
  const svcRest = svcSorted.slice(5);
  const othersValue = svcRest.reduce((s, x) => s + x.value, 0);

  const serviceWiseData = [
    ...svcTop,
    ...(othersValue > 0 ? [{ name: "Others", value: othersValue }] : []),
  ].map((item, i, arr) => ({
    ...item,
    pct: svcTotal ? Math.round((item.value / svcTotal) * 100) : 0,
    // "Others" always gets the grey color, rest cycle through colors
    color: item.name === "Others" ? "#bfbfbf" : svcColors[i % svcColors.length],
  }));

  // ---- Top Customers by Quotation Value ----
  const custMap = {};
  dashData.forEach(d => {
    const key = d.customer_company || d.customer_name || "Unknown";
    custMap[key] = (custMap[key] || 0) + Number(d.grand_total || 0);
  });
  const topCustomers = Object.entries(custMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);
  const topCustMax = Math.max(...topCustomers.map(c => c.value), 1);
  const custColors = ["#1677ff", "#fa8c16", "#722ed1", "#13c2c2", "#52c41a"];

  // ---- Expiring Quotations (by expected_close_date) ----
  const expiringQuotations = dashData
    .filter(d => d.expected_close_date)
    .map(d => ({
      qu_no: d.qu_no,
      customer: d.customer_company || d.customer_name || "-",
      value: Number(d.grand_total || 0),
      validTill: dayjs(d.expected_close_date),
    }))
    .sort((a, b) => a.validTill.valueOf() - b.validTill.valueOf());

  const row1 = [
    { title: "Draft Quotations", value: draftCount, color: "#1677ff", bg: "#f0f7ff", icon: <FileTextOutlined /> },
    { title: "Sent Quotations", value: sentCount, color: "#13c2c2", bg: "#f0fffe", icon: <SendOutlined /> },
    { title: "Viewed Quotations", value: viewedCount, color: "#722ed1", bg: "#f9f0ff", icon: <EyeOutlined /> },
    { title: "Approved Quotations", value: approvedCount, color: "#52c41a", bg: "#f6ffed", icon: <CheckCircleOutlined /> },
    { title: "Rejected Quotations", value: rejectedCount, color: "#f5222d", bg: "#fff1f0", icon: <CloseCircleOutlined /> },
  ];

  const row2 = [
    { title: "Total Quotation Value", value: `₹ ${paymentTotals.totalValue.toLocaleString('en-IN')}`, sub: "All quotations", color: "#f5222d", bg: "#fff1f0", icon: <WalletOutlined /> },
    { title: "Expected Revenue", value: `₹ ${paymentTotals.expectedRevenue.toLocaleString('en-IN')}`, sub: "From converted quotations", color: "#52c41a", bg: "#f6ffed", icon: <LineChartOutlined /> },
    { title: "Collected Amount", value: `₹ ${paymentTotals.collected.toLocaleString('en-IN')}`, sub: "From approved quotations", color: "#1677ff", bg: "#f0f7ff", icon: <BankOutlined /> },
    { title: "Pending Amount", value: `₹ ${paymentTotals.pending.toLocaleString('en-IN')}`, sub: "Yet to be collected", color: "#fa8c16", bg: "#fff7e6", icon: <ClockCircleOutlined /> },
    { title: "Conversion Rate", value: `${conversionRate}%`, sub: "Approved / Total", color: "#722ed1", bg: "#f9f0ff", icon: <LineChartOutlined /> },
  ];

  const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
          {sub && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{sub}</div>}
        </div>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
          {icon}
        </div>
      </div>
    </Card>
  );
  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }}>
      {/* Header */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Quotation Dashboard</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Quotation Dashboard</Typography.Text>
        </div>
        <Space>
          <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
            <Button
              size="middle" type="primary" icon={<PlusOutlined />}
              onClick={() => navigate('/quotation/create')} disabled={!perm.add}
              style={{ background: '#1677ff', borderColor: '#1677ff' }}
            >
              Create Quotation
            </Button>
          </Tooltip>

          <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
            <Button
              size="middle" type="primary" icon={ <PlusOutlined />}
              onClick={() => navigate('/quotation/create')} disabled={!perm.add}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Create Revision
            </Button>
          </Tooltip>

          <Tooltip title={!invoicePerm.add ? "You don't have permission to create invoice" : ""}>
            <Button
              size="middle" type="primary" icon={<PlusOutlined />}
              onClick={() => navigate('/invoice/create')} disabled={!invoicePerm.add}
              style={{ background: '#722ed1', borderColor: '#722ed1' }}
            >
              Convert To Invoice
            </Button>
          </Tooltip>
          <Dropdown
            trigger={['hover']}
            overlay={
              <Menu
                onClick={({ key }) => {
                  let range;
                  let label;
                  if (key === 'today') {
                    range = [dayjs().startOf('day'), dayjs().endOf('day')];
                    label = 'Today';
                  } else if (key === 'last7') {
                    range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                    label = 'Last 7 Days';
                  } else if (key === 'last30') {
                    range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                    label = 'Last 30 Days';
                  } else if (key === 'lastMonth') {
                    range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                    label = 'Last Month';
                  } else if (key === 'thisYear') {
                    range = [dayjs().startOf('year'), dayjs()];
                    label = 'This Year';
                  }
                  setDashboardDateRange(range);
                  setDashboardDateLabel(label);
                }}
                items={[
                  { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                  { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                  { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                  { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                  { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                ]}

              />
            }
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button size="middle" icon={<DownOutlined />}>
                More Actions
              </Button>
            </div>
          </Dropdown>
        </Space>
      </div>

      {/* Dashboard date range below More Actions */}
      <div style={{ padding: '8px 16px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <DatePicker.RangePicker
          value={dashboardDateRange}
          format="DD MMM YYYY"
          allowClear={true}
          size="middle"
          style={{ width: 240 }}
          onChange={(vals) => {
            if (vals) {
              setDashboardDateRange(vals);
              setDashboardDateLabel(`${vals[0].format('DD MMM YYYY')} → ${vals[1].format('DD MMM YYYY')}`);
            } else {
              setDashboardDateRange([dayjs().startOf('month'), dayjs()]);
              setDashboardDateLabel(null);
            }
          }}
        />

      </div>

      {/* Stat Cards */}
      {/* Stat Cards */}
      <div style={{ padding: '8px 16px 0' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          {row1.map((card, i) => (
            <div key={i} style={{ flex: '1 1 0', minWidth: 180 }}>
              <StatCard {...card} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {row2.map((card, i) => (
            <div key={i} style={{ flex: '1 1 0', minWidth: 180 }}>
              <StatCard {...card} />
            </div>
          ))}
        </div>
      </div>

      {/* Analytics Panels */}
      <div style={{ padding: '6px 16px 0' }}>
        <Row gutter={[8, 8]}>
          {/* Quotation Pipeline */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Quotation Pipeline</Typography.Text>
              <div style={{ marginTop: 6 }}>
                {pipeline.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ flex: 1, position: 'relative', height: 22 }}>
                      <div style={{
                        position: 'absolute', inset: 0,
                        width: `${Math.max((p.count / pipelineMax) * 100, 28)}%`,
                        background: `${p.color}1f`,
                        borderRadius: 5,
                      }} />
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', padding: '0 10px' }}>
                        <span style={{ fontSize: 12, color: '#333' }}>{p.label}</span>
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#222', minWidth: 22, textAlign: 'right' }}>{p.count}</span>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          {/* Monthly Quotation Value */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong>Monthly Quotation Value</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>(in ₹)</Typography.Text>
              </div>
              {monthlyData.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 150, color: '#bbb', fontSize: 13 }}>
                  <span style={{ fontSize: 28 }}>📈</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ width: '100%', height: 160, marginTop: 8 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="quVal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1677ff" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#1677ff" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} width={40}
                        tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <RechartsTooltip
                        contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }}
                        formatter={(v) => [`₹ ${Number(v).toLocaleString('en-IN')}`, 'Value']}
                      />
                      <Area type="monotone" dataKey="value" stroke="#1677ff" strokeWidth={2} fill="url(#quVal)" dot={{ r: 4, fill: '#1677ff' }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>

          {/* Quotation Status Distribution */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Quotation Status Distribution</Typography.Text>
              {distTotal === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13, marginTop: 8 }}>
                  <span style={{ fontSize: 28 }}>📊</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                  <div style={{ width: 120, height: 120, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <RechartsTooltip
                          contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }}
                          formatter={(value, name) => [`${value} (${distTotal ? Math.round((value / distTotal) * 100) : 0}%)`, name]}
                        />
                        <Pie data={statusDistData} dataKey="value" nameKey="name" innerRadius={32} outerRadius={55} paddingAngle={2}>
                          {statusDistData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {statusDistData.map((e, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 5 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: e.color, fontSize: 14 }}>●</span>
                          <span style={{ color: '#333' }}>{e.name}</span>
                        </span>
                        <span style={{ fontWeight: 700, color: '#333' }}>{distTotal ? Math.round((e.value / distTotal) * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Second analytics row */}
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          {/* Service Wise Quotation Value */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Service Wise Quotation Value</Typography.Text>
              {svcTotal === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13, marginTop: 8 }}>
                  <span style={{ fontSize: 28 }}>📊</span>
                  <span style={{ marginTop: 6 }}>No data yet</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                  <div style={{ width: 120, height: 120, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <RechartsTooltip
                          contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }}
                          formatter={(value, name) => [`₹ ${Number(value).toLocaleString('en-IN')} (${svcTotal ? Math.round((value / svcTotal) * 100) : 0}%)`, name]}
                        />
                        <Pie data={serviceWiseData} dataKey="value" nameKey="name" innerRadius={32} outerRadius={55} paddingAngle={2}>
                          {serviceWiseData.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {serviceWiseData.map((e, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 5 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: e.color, fontSize: 14 }}>●</span>
                          <span style={{ color: '#333' }}>{e.name} <span style={{ color: '#888' }}>({e.pct}%)</span></span>
                        </span>
                        <span style={{ fontWeight: 600, color: '#333' }}>₹ {e.value.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>

          {/* Top Customers by Quotation Value */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <Typography.Text strong>Top Customers by Quotation Value</Typography.Text>
              <div style={{ marginTop: 10 }}>
                {topCustomers.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>No data yet</Typography.Text>
                ) : (
                  topCustomers.map((c, i) => (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                        <span style={{ color: '#333' }}>{c.name}</span>
                        <span style={{ fontWeight: 700, color: '#333' }}>₹ {c.value.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                        <div style={{ width: `${Math.max((c.value / topCustMax) * 100, 4)}%`, height: '100%', background: custColors[i % custColors.length], borderRadius: 3 }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>

          {/* Expiring Quotations */}
          <Col xs={24} lg={8}>
            <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text strong>Expiring Quotations</Typography.Text>
                {expiringQuotations.length > 3 && (
                  <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }}>View All</Button>
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                {expiringQuotations.length === 0 ? (
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>No expiring quotations</Typography.Text>
                ) : (
                  <>
                    {expiringQuotations.slice(0, 3).map((q, i, arr) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 0 }}>
                          <ExclamationCircleOutlined style={{ color: '#f5222d', marginTop: 3, flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1677ff' }}>{q.qu_no}</div>
                            <div style={{ fontSize: 12, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.customer}</div>
                            <div style={{ fontSize: 11, color: '#bbb' }}>Valid Till: {q.validTill.format('DD MMM YYYY')}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#333', margin: '0 14px', whiteSpace: 'nowrap' }}>
                          ₹ {q.value.toLocaleString('en-IN')}
                        </div>
                        <Button
                          size="small"
                          onClick={() => navigate('/quotation/edit', { state: { qu_no: q.qu_no } })}
                          style={{ flexShrink: 0, background: '#eef2ff', borderColor: '#eef2ff', color: '#4f46e5', fontWeight: 600, fontSize: 12 }}
                        >
                          Send Reminder
                        </Button>
                      </div>
                    ))}

                  </>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      <div style={{ padding: '8px 16px 16px' }}>
        {msgContext}

        {/* 🔹 Segment Tabs */}
        {/* <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <Segmented
          value={segment}
          size="large"
          onChange={handleSegmentChange}
          options={[
            {
              label: (
                <span style={{ color: "green" }}>
                  Active <Badge count={statusCounts["Active"] || 0} style={styles.badge} />
                </span>
              ),
              value: "Active",
            },
            {
              label: (
                <span style={{ color: "red" }}>
                  Archive <Badge count={statusCounts["Archive"] || 0} style={styles.badge} />
                </span>
              ),
              value: "Archive",
            },
            {
              label: (
                <span style={{ color: "blue" }}>
                  Draft <Badge count={statusCounts["Draft"] || 0} style={styles.badge} />
                </span>
              ),
              value: "Draft",
            },
          ]}
          style={{ fontSize: 16, fontWeight: 500 }}
        />
      </div> */}

        {/* 🔹 Search + Date Range */}
        {/* <DashboardSummary
        quickStats={quickStats}
        followUpStats={followUpStats}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        applyQuickDateFilter={applyQuickDateFilter}
        applyFollowUpDateFilter={applyFollowUpDateFilter}
        clearDateFilters={clearDateFilters}
      /> */}

        <Divider />

        {/* 🔹 Payment / Ageing / Activities */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>

          {/* Payment Collection Summary */}
          <Col xs={24} lg={8}>
            <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: 12, height: '100%', border: '1px solid #f0f0f0' }}>
              <Typography.Text strong style={{ fontSize: 14, color: '#222' }}>Payment Collection Summary</Typography.Text>
              <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
                {[
 {
                  label: "Collected This Month",
                  value: allPayments
                    .flatMap(inv => (inv.payments || []).map(p => ({ ...p, source_qu_no: inv.source_qu_no || inv.qu_no })))
                    .filter(p => {
                      const quNos = new Set(dashData.map(d => d.qu_no));
                      return quNos.has(p.source_qu_no) && dayjs(p.payment_date || p.added_on).isSame(dayjs(), 'month');
                    })
                    .reduce((s, p) => s + Number(p.paid_amt || 0), 0),
                  color: "#52c41a", bg: "#f6ffed", iconBg: "#d9f7be", icon: <BankOutlined style={{ fontSize: 18 }} />,
                },
                {
                  label: "Pending Collection",
                  value: paymentTotals.pending,
                  color: "#fa8c16", bg: "#fff7e6", iconBg: "#ffe7ba", icon: <ClockCircleOutlined style={{ fontSize: 18 }} />,
                },
                {
                  label: "Overdue Amount",
                  value: data.filter(d => {
                    const st = (d.quotation_status || "").toLowerCase();
                    const v = d.valid_date || d.expected_close_date;
                    return st === "converted" && v && dayjs(v).isBefore(dayjs());
                  }).reduce((s, d) => {
                    const collected = collectedForQuotation(d.qu_no);
                    return s + Math.max(Number(d.grand_total || 0) - collected, 0);
                  }, 0),
                  color: "#f5222d", bg: "#fff1f0", iconBg: "#ffccc7", icon: <ExclamationCircleOutlined style={{ fontSize: 18 }} />,
                },
                ].map((item, i) => (
                  <Col span={8} key={i}>
                    <div style={{
                      background: '#fff',
                      borderRadius: 10,
                      padding: '12px 10px',
                      textAlign: 'center',
                      border: '1px solid #f0f0f0',
                      height: '100%',
                    }}>
                      <div style={{ fontSize: 11, color: '#999', marginBottom: 6, lineHeight: 1.3 }}>{item.label}</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: item.color, marginBottom: 10 }}>
                        ₹ {Number(item.value || 0).toLocaleString('en-IN')}
                      </div>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: item.iconBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto', color: item.color,
                      }}>
                        {item.icon}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          {/* Quotation Ageing */}
          <Col xs={24} lg={8}>
            <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: 12, height: '100%', border: '1px solid #f0f0f0' }}>
              <Typography.Text strong style={{ fontSize: 14, color: '#222' }}>Quotation Ageing (Pending)</Typography.Text>
      {(() => {
                const today = dayjs();
                // Track real pending BALANCE (quoted value minus amount actually
                // collected via linked invoice payments) for Converted quotations,
                // matching the "Pending Amount" stat card above — instead of the
                // full quoted value of unrelated Draft/Sent/Viewed/Negotiation rows.
                const pending = dashData.filter(d =>
                  (d.quotation_status || "").toLowerCase() === "converted"
                  && collectedForQuotation(d.qu_no) < Number(d.grand_total || 0)
                );
                const buckets = [
                  { label: "0 - 15 Days", color: "#1677ff", bg: "#f0f7ff", min: 0, max: 15 },
                  { label: "16 - 30 Days", color: "#1677ff", bg: "#e6f4ff", min: 16, max: 30 },
                  { label: "31 - 60 Days", color: "#fa8c16", bg: "#fff7e6", min: 31, max: 60 },
                  { label: "Above 60 Days", color: "#f5222d", bg: "#fff1f0", min: 61, max: 99999 },
                ];
                return (
                  <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
                    {buckets.map((b, i) => {
                      const val = pending
                        .filter(d => {
                          const age = today.diff(dayjs(d.added_on), 'day');
                          return age >= b.min && age <= b.max;
                        })
                        .reduce((s, d) => s + Math.max(Number(d.grand_total || 0) - collectedForQuotation(d.qu_no), 0), 0);
                      return (
                        <Col span={12} key={i}>
                          <div style={{
                            background: b.bg,
                            borderRadius: 10,
                            padding: '14px 12px',
                            border: `1px solid ${b.color}22`,
                          }}>
                            <div style={{ fontSize: 12, color: b.color, fontWeight: 600, marginBottom: 6 }}>{b.label}</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: b.color }}>
                              ₹ {val.toLocaleString('en-IN')}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                );
              })()}
            </Card>
          </Col>

          {/* Recent Activities */}
          {/* Recent Activities */}
          <Col xs={24} lg={8}>
            <Card
              bodyStyle={{ padding: '0 14px 10px' }}
              style={{ borderRadius: 12, height: '100%', border: '1px solid #f0f0f0' }}
              title={<Typography.Text strong style={{ fontSize: 14, color: '#222' }}>Recent Activities</Typography.Text>}
              extra={<Button type="link" size="small" style={{ padding: 0, fontSize: 12, color: '#1677ff', fontWeight: 600 }}>View All</Button>}
              headStyle={{ padding: '12px 16px', minHeight: 44, borderBottom: '1px solid #f5f5f5' }}
            >
              {(() => {
                const activities = [...dashData]

                  .sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on))
                  .slice(0, 5)
                  .map(d => {
                    const st = (d.quotation_status || "Draft").toLowerCase();
                    const colorMap = {
                      draft: '#8c8c8c', sent: '#1677ff', viewed: '#722ed1',
                      negotiation: '#fa8c16', approved: '#52c41a',
                      converted: '#13c2c2', rejected: '#f5222d',
                    };
                    const iconMap = {
                      sent: <SendOutlined />, approved: <CheckCircleOutlined />,
                      converted: <CheckCircleOutlined />, rejected: <CloseCircleOutlined />,
                      negotiation: <ExclamationCircleOutlined />,
                    };
                    const color = colorMap[st] || '#1677ff';
                    const icon = iconMap[st] || <FileTextOutlined />;
                    const updatedOn = dayjs(d.updated_on);
                    const isToday = updatedOn.isSame(dayjs(), 'day');
                    const isYesterday = updatedOn.isSame(dayjs().subtract(1, 'day'), 'day');
                    const timeStr = isToday
                      ? `Today, ${updatedOn.format('hh:mm A')}`
                      : isYesterday
                        ? `Yesterday, ${updatedOn.format('hh:mm A')}`
                        : updatedOn.format('DD MMM YYYY');
                    const st0 = (d.quotation_status || 'Draft');
                    const cust = d.customer_name || d.customer_company || '-';
                    const actionText = `${st0} — ${cust}`;
                    return { qu_no: d.qu_no, actionText, color, icon, timeStr };
                  });

                if (!activities.length) return (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>No activities</Typography.Text>
                );

                return (
                  <div>
                    {activities.map((a, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '5px 0',
                        borderBottom: i < activities.length - 1 ? '1px solid #f5f5f5' : 'none',
                      }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: 6,
                          background: `${a.color}18`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, color: a.color, fontSize: 12,
                        }}>
                          {a.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span style={{ color: '#1677ff', fontWeight: 600 }}>{a.qu_no}</span>
                            {' '}<span style={{ color: '#666' }}>{a.actionText}</span>
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: '#bbb', flexShrink: 0, textAlign: 'right', minWidth: 80 }}>{a.timeStr}</div>
                      </div>
                    ))}
                    <div style={{ textAlign: 'center', marginTop: 8, borderTop: '1px solid #f5f5f5', paddingTop: 6 }}>
                      <Button type="link" size="small" style={{ fontSize: 12, color: '#1677ff', fontWeight: 600, padding: 0 }}>
                        View All Activities
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </Card>
          </Col>
        </Row>

        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
            <Input
              allowClear
              placeholder="Search Quotations..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ flex: 2, minWidth: 140 }}
            />
            <Select
              allowClear
              placeholder="All Status"
              style={{ flex: 1, minWidth: 110 }}
              value={quotationStatusFilter}
              options={[
                { label: "Draft", value: "draft" },
                { label: "Sent", value: "sent" },
                { label: "Viewed", value: "viewed" },
                { label: "Negotiation", value: "negotiation" },
                { label: "Approved", value: "approved" },
                { label: "Converted", value: "converted" },
                { label: "Rejected", value: "rejected" },
              ]}
              onChange={(val) => {
                setQuotationStatusFilter(val);
                applyAllFilters(data, segment, searchText, dateRange, dateFilterType, val, salesExecFilter, serviceFilter);
              }}
            />
            <Select
              allowClear
              placeholder="All Services"
              style={{ flex: 1, minWidth: 110 }}
              value={serviceFilter}
              options={serviceOptions}
              onChange={(val) => {
                setServiceFilter(val);
                applyAllFilters(data, segment, searchText, dateRange, dateFilterType, quotationStatusFilter, salesExecFilter, val);
              }}
            />
            <Select
              allowClear
              placeholder="All Sales Executives"
              style={{ flex: 1, minWidth: 130 }}
              value={salesExecFilter}
              options={salesExecOptions}
              onChange={(val) => {
                setSalesExecFilter(val);
                applyAllFilters(data, segment, searchText, dateRange, dateFilterType, quotationStatusFilter, val, serviceFilter);
              }}
            />
            <DatePicker.RangePicker
              onChange={handleDateRangeChange}
              value={dateRange}
              format="DD-MM-YYYY"
              style={{ flex: 2, minWidth: 200 }}
            />
<Button icon={<FilterOutlined />} onClick={clearDateFilters}>Clear</Button>
            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
              <Button icon={<DownloadOutlined />}>Export</Button>
            </Dropdown>
          </div>
        </Card>

        {/* 🔹 Table */}
        {/* 🔹 Table */}
        <Table
          scroll={{ x: "max-content" }}
          dataSource={filteredData.filter(d => {
            if (!dashboardDateRange) return true;
            const date = dayjs(d.added_on);
            return date.isValid()
              && date.isSameOrAfter(dashboardDateRange[0], 'day')
              && date.isSameOrBefore(dashboardDateRange[1], 'day');
          })}
          columns={columns}
          rowKey="qu_no"
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => getQuotationFollowUps(record).length > 0,
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
            showExpandColumn: false, // Hide default + icon
          }}
        />
      </div>

    </Card>
  );
};

export default QuotationPage;
